import './Networking.css';
import NetworkingOpt from "../../components/seo/NetworkingOpt";

export default function NetworkServices() {
    return (
        <>
            <NetworkingOpt
                title="Network Services"
                description="Review network services including ACLs, NTP, DNS, DHCP, SNMP, Syslog, FTP, NAT, QoS, and related configuration examples."
                keywords="network services, acl, ntp, dns, dhcp, snmp, syslog, ftp, nat, qos"
            />

            <article className='content'>
                <h1 style={{display:'flex', justifyContent:'center'}}>Network Services</h1>

                <h2>Access Control Lists (ACL)</h2>

                <p>ACL functions as a packet filter, instructing the router to permit or discard specific traffic.</p>
                <p>ACL can filter traffic based on source/destination IP addresses, source/destination layer 4 ports etc.</p>
                <p>ACLs are an ordered sequence of Access Control Entries (ACE).</p>
                <p>ACLs are configured globally on the router, (global config mode). It will not make ACL take effect, configuration must be applied to interface either inbound or outbound. </p>
                <p>ACLs are made up of one or more ACEs. When the router checks the packet against the ACL , it processes the ACEs in order from top to bottom.</p>
                <p>If the packet matches one of the ACEs in the ACL, the router takes the action and stops processing the ACL. All entries below the matching entry will be ignored.</p>
                <p>Implicit ‘deny’: The implicit deny tells the router to deny all traffic that doesn’t match any of the configured entries in the ACL. There is an implicit deny at the end of all ACLs.</p>
                
                <h3>ACL Types: </h3>

                <h4>Standard ACLs</h4>
                <p>Match based on source IP address only. </p>

                <h5>Standard Numbered ACLs: </h5>
                <p>Numbered ACLs are identified with a number (i.e ACL1, ACL2 etc). Different types of ACLs have a range of numbers that can be used. </p>
                <p>Standard ACLs can use 1-99 and 1300-1999.</p>
                <p>The basic command to configure a standard numbered ACL is: </p>
                <pre>
                    <code>R1(config)# access-list number deny / permit ip wildcard-mask</code><br />
                    <code>R1(config-if)# ip access-group number</code>
                </pre>
                
                <h5>Standard Named ACLs:</h5>
                <p>Named ACLs are identified with a name (i.e ‘Block-BOB’).</p>
                <p>Standard Named ACLs are configured by entering ‘standard named ACL config mode’, and then configuring each entry within that config mode. </p>
                <pre>
                    <code>@R1(config)# ip access-list standard acl-name</code><br />
                    <code>@R1(config-std-nacl)# [entry-number]  deny / permit ip wildcard-mask </code>
                </pre>
    
                <h4>Extended ACLs</h4>
                <p>Match based on source/destination IP, source/destination port etc.</p>
                <p>Numbered ACLs are configured in a global config mode. @R1(config)# access-list number [permit/deny] protocol  src-ip  dest-ip</p>
                <p>Named ACLs are configured with subcommands in a separate config mode.</p>
                <pre>
                    <code>@R1(config)# ip access-list extended name | number</code><br />
                    <code>@R1(config-ext-nacl)# [seq-num] permit | deny protocol src-ip dest-ip</code>
                </pre>
                <p>However, in modern IOS Numbered ACLs are configured in the same way as Named ACLs.</p>
                
                <p>Advantages of named ACL config mode:</p>
                <p>Individual entries with no entry-number can be deleted. </p>
                <p>When configuring/editing numbered ACLs from global config mode, only entire ACL can be deleted not individual entries.</p>
                
                <h3>Resequencing ACLs</h3>
                <p>Command is: @ ip access-list resequence acl-id  starting-seq-num increment</p>
                <p>IP Protocol Number </p>
                <p>ICMP: 1 </p>
                <p>TCP: 6 </p>
                <p>UDP: 17 </p>
                <p>EIGRP: 88 </p>
                <p>OSPF: 89 </p>
                
                <h3>Extended ACL entry practice</h3>
                <p>Allow all traffic: permit ip any any</p>
                <p>Prevent 10.0.0.0/16 from sending UDP traffic to 192.168.1.1/32. </p>
                <pre>@deny udp 10.0.0.0 0.0.255.255 host 192.168.1.1</pre>
                <p>Prevent 172.16.1.1/32 from pinging hosts in 192.168.0.0/24.</p>
                <pre>@deny icmp host 172.16.1.1 192.168.0.0 0.0.0.255.</pre>
                

                <h2>Network Time Protocol (NTP) </h2>

                <p>It is an internal clock of a device. @show clock command</p>
                <p>Accurate time is required to have accurate logs for troubleshooting.</p>
                <p>Hardware clock is builtin and is the default time-source. It is separate from software clock.</p>
                <p>NTP allows automatic syncing of time over a network.</p>
                <p>NTP clients request the time from NTP servers. A device can be NTP server and an NTP client at the same time.</p>
                <p>NTP allows accuracy of time within ~1 ms in same LAN, ~50 ms in WAN.</p>
                <p>The distance of an NTP servers from the original reference clock is called stratum.</p>
                <p>NTP uses UDP port 123 to communicate.</p>
                
                <h3>Manual time configuration: </h3>

                <p>Configuring time: @clock set</p>
                <p>Hardware Clock(calendar) configuration: @clock-calendar set </p>
                <p>Synchronize the ‘clock’ and ‘calendar’: @clock update-calendar</p>
                <p>Configuring timezone: @clock timezone</p>
                <p>Daylight Saving Time(Summer Time):</p>
                <pre>@clock summer-time EDT recurring 2 sunday march 02:00 1 sun</pre>
               
                <h3>Reference Clocks:</h3>

                <p>A reference clock is like an atomic clock or GPS clock.</p>
                <p>Reference clocks are stratum 0 within the NTP hierarchy.</p>
                <p>NTP servers directly connected to reference clocks are stratum 1.</p>
                
                <h3>NTP hierarchy:</h3>

                <p>Stratum 2: NTP Servers get their time from stratum 1 NTP servers. </p>
                <p>Stratum 15: It is the maximum anything above that is considered unreliable.</p>
                <p>Devices can also ‘peer’ with devices at the same stratum to provide more accurate time.</p>
                <p>An NTP client can sync to multiple NTP servers.</p>
                
                <h3>NTP Configuration: </h3>

                <p>NTP uses only the UTC timezone.</p>
                <p>Configuring NTP Server Mode: @R1(config)# NTP master ?</p>
                <p>Configuring NTP Symmetric active mode: @R1(config)# ntp peer peer-add</p>
                <p>Configuring NTP Authentication: It allows NTP clients to ensure they only sync to intended servers.</p>
                
                <h4>ntp authenticate </h4>
                <pre>
                    <code>@ntp authentication-key key-number md5 key</code><br />
                    <code>@ntp trusted-key key-number</code><br />
                    <code>@ntp server ip-address key key-number</code><br />
                </pre>
                
                
                <h2>Domain Name System (DNS)</h2>

                <p>DNS is used to resolve human-readable names (google.com) to IP address. Device will ask DNS server for the IP address if the URL.</p>
                <p>DNS Server can be manually configured or learned via DHCP. </p>
                <p>Standard DNS queries/responses typically uses UDP. TCP is used for DNS messages greater than 512 bytes.</p>
                <p>It uses both TCP and UDP port 53.</p>
                <p>DoT: DNS over TLS</p>
                
                <p>Nslookup: to know ip address. </p>
                <p>DNS Cache: C:/Users/User&gt; ipconfig/displaydns &gt;ipconfig /flushdns -Devices will save the server’s responses to a local DNS cache.</p>
                <p>DNS in Cisco IOS: Hosts in a network will simply forward the DNS messages like any other packets. A cisco router can also be configured as a DNS client.</p>
                
                
                <h2>Dynamic Host Configuration Protocol (DHCP)</h2>

                <p>DHCP is used to allow a hosts to automatically/dynamically learn various aspects of their network configuration, such as IP address, subnet mask, default gateway, DNS server etc without manual/static configuration.</p>
                <p>DHCP server ‘lease’ IP address to clients.</p>
                <p>These leases are usually not permanent and the client must give up the address at the end of the lease.</p>
                <p>DHCP Server use UDP 67.</p>
                <p>DHCP Client use UDP 68.</p>
                <p>Troubleshooting: ipconfig/release and ipconfig/renew</p>
                
                <h3>DHCP Discover Message:</h3>

                <p>DHCP Discover: client-server, Broadcast</p>
                <p>DHCP Offer: server-client, bootp flags: 0x0000(Unicast or Broadcast)</p>
                <p>DHCP Request: client-server, Broadcast</p>
                <p>DHCP Ack: server-client, Broadcast or Unicast</p>
                <p>DHCP Release: client-server, Unicast</p>
                <p>DHCP Relay: DHCP Relay agent will forward the clients broadcast DHCP messages to the remote DHCP server as unicast messages in a centralized DHCP Server because broadcast messages don’t leave the local subnet.</p>
                
                <h3>DHCP Server Configuration in IOS</h3>

                <pre>
                    <code>@R1(config)# ip dhcp excluded-address 192.168.1.1 192.168.1.10</code><br />
                    <code>@R1(config)# ip dhcp pool LAB_POOL</code><br />
                    <code>@R1(dhcp-config)# network 192.168.1.0/24</code><br />
                    <code>@R1(dhcp-config)# dns-server 8.8.8.8</code><br />
                    <code>@R1(dhcp-config)# domain-name explorelrt.com</code><br />
                    <code>@R1(dhcp-config)# default-router 192.168.1.1</code><br />
                    <code>@R1(dhcp-config)# lease 5 30</code>(lease time in days hours minutes or lease infinite)
                </pre>
                

                <h2>Simple Network Management Protocol (SNMP)</h2>

                <p>SNMP is a industry standard framework and protocol released in 1988. </p>
                <ul>
                    <li>RFC 1065: Structure and identification of management information for TCP/IP based internets </li>
                    <li>RFC 1066: Management information base for network management of TCP/IP based internet. </li>
                    <li>RFC 1067: A simple network management protocol.</li>
                </ul>
                <p>SNMP can be used to monitor the status of devices, make configuration changes etc. </p>
                <p>Consists of Managed devices(router and switches) and Network Management Station(NMS) which is the SNMP ‘server’.</p>
                <p>The operations used in SNMP are: Managed devices can notify the NMS of events and the NMS can tell the managed devices to change aspects of their configuration.</p>
                
                <h3>SNMP Components: </h3>

                <p>The SNMP Manager is the software on the NMS that interacts with the managed devices. It receives notifications, sends requests for information, sends configuration changes etc.</p>
                <p>The SNMP Application provides an interface for the network admin to interact with.</p>
                <p>The SNMP Agent is the software running on the managed devices that interacts with the SNMP manager on the NMS. It sends notifications to/receives messages from NMS.</p>
                <p>The Management Information Base (MIB) is the structure that contains the variables that are managed by SNMP. Each variable is identified with an object ID (OID).  </p>
                <p>SNMP OIDs:</p>
                <p>SNMP object IDs are organised in hierarchical structure.</p>

                <h4>SNMP Versions: </h4>
                <p>#SNMPv1 </p>
                <p>#SNMPv2: Allows the NMS to retrieve large amounts of information in a single request, so it is more efficient. C refers to community string.</p> 
                <p>#SNMPv3: Strong encryption and authentication.</p>

                <h4>SNMP Messages: </h4>
                <p>SNMP Agent= UDP 161 and SNMP Manager = UDP 162</p>
                <p>Trap is a notification sent from agent to the manager. </p>
                
                <h2>Syslog</h2>

                <p>Syslog is industry standard protocol for message logging on a network devices.</p>
                <p>Syslog can be used to log events such as changes in interface status, changes in OSPF neighbour status etc.</p>
                <p>The messages can be displayed in the CLI, saved in device RAM, or sent to an external syslog server.</p>
                <p>Logs are essential when troubleshooting issues, examining the cause of incidents etc.</p>
                
                <h3>Syslog message format</h3>

                <p>Seq: time stamp : %facility-severity-MNEMONIC : description</p>
                <p>Facility = which process on the device generated the message.</p>
                <p>MNEMONIC = A short code for the message indicating what happened.</p>

                <h3>Syslog Severity Levels</h3>      

                <table>
                    <tr><td>Level Number</td><td>Severity Name</td><td>Description</td><td>Typical Use</td></tr>
                    <tr><td>0</td><td>Emergency</td><td>System is unusable</td><td>Kernel panic, catastrophic failure</td></tr>
                    <tr><td>1</td><td>Alert</td><td>Immediate action required</td><td>Database corruption, power failure</td></tr>
                    <tr><td>2</td><td>Critical</td><td>Critical conditions</td><td>Hardware errors, disk full</td></tr>
                    <tr><td>3</td><td>Error</td><td>Error conditions</td><td>Failed service start, interface down</td></tr>
                    <tr><td>4</td><td>Warning</td><td>Warning conditions</td><td>High CPU usage, nearing capacity</td></tr>
                    <tr><td>5</td><td>Notice</td><td>Normal but significant</td><td>Configuration changes, successful backup</td></tr>
                    <tr><td>6</td><td>Informational</td><td>Informational messages</td><td>Service start/stop, user login</td></tr>
                    <tr><td>7</td><td>Debug</td><td>Debug-level messages</td><td>Detailed troubleshooting info</td></tr>
                </table>

                <h4>How It Works</h4>
                <p>Each message logged by a device includes a facility code (source of the message) and a severity level (importance).</p>
                <p>Administrators can filter logs based on severity using commands like: logging trap warning</p>
                <p>This example sends only messages with severity 4 (Warning) and above to the Syslog server.</p>
                <p>“Because severities are very subjective, a relay or collector should not assume that all originators have the same definition of severity.” -RFC 5424</p>
                
                <h4>Syslog Logging Locations</h4>
                <p>Console Line: Syslog message will be displayed in the CLI when connected to the device via the console port. By default, all messages (Level 0 - Level 7) are displayed.</p>
                <p>VTY Lines: Syslog messages will be displayed in the CLI when connected to the device via Telnet/SSH. Disabled by default.</p>
                <p>Buffer: Syslog messages will be saved to RAM. - Show logging</p>
                <p>External Server: Syslog server will listen for messages on UDP port 514. </p>
                
                <h4>Terminal Monitor</h4>
                <p>Even if logging monitor level is enabled,  by default syslog messages will not be displayed when connected via Telnet or SSH.</p>
                <p>The following command must be used for the messages to be displayed. </p>
                <pre>R1# terminal monitor</pre>
                <p>Logging Synchronous to prevent displaying other logging messages in the middle of typing.</p>
                <p>Service timestamps / Service sequence-numbers</p>
                <pre>R1(config)# service timestamps log datetime  </pre>


                <h2>Secure Shell (SSH)</h2>

                <p>By default, no password needed to access the CLI of a CISCO.</p>
                <p>Secure Shell was developed in 1995 to replace less secure protocols like Telnet. </p>
                <p>Provides security features such as encryption and authentication.</p>
                <p>SSH Server uses TCP port 22.</p>
                
                <p>Console port security- login local</p>
                <pre>
                    <code>R1(config)# username admin secret ccna</code><br />
                    <code>R1(config)# line console 0</code><br />
                    <code>R1(config-line)# login local </code>
                </pre>
                
                <p>Layer-2-switch-management IP</p>
                <p>Layer 2 switches do not perform packet routing and don’t build a routing table. They aren’t IP routing aware.</p>
                <p>However, IP address can be assigned to an SVI to allow remote connections to the CLI of the switch (using telnet and SSH). (SVI = Switch Virtual Interface)</p>
                
                <h3>SSH Configuration</h3>
                <pre>SW1# show ip ssh</pre>
                <p>IOS images that support SSH will have ‘k9’ in their name.</p>
                <p>No Payload Encryption(NPE) IOS images do not support cryptographic features such as SSH.</p>
                
                <h3>RSA Keys</h3>
                <p>RSA public and public key pair needed.</p>
                <p>Fully Qualified Domain Name (FQDN) of the device is used to name the RSA keys. </p>
                <pre>SW1# crypto key generate rsa</pre>
                <p>1. Configure host name</p>
                <p>2. Configure DNS domain name</p>
                <p>3. Generate RSA key pair</p>
                <p>4. Configure enable pw, username/pw</p>
                <p>5. Enable SSHv2 (only) </p>
                <p>6. Configure VTY lines</p>
                <p>Connect: ssh-1 username ip-address OR ssh username@ip-address.</p>
                
                <h2>Telnet</h2>
                <p>Teletype Network (Telnet) is a protocol used to remotely access the CLI of a remote host.</p>
                <p>No encryption and sends data in plain text, uses TCP port 23.</p>
                
                <h3>Telnet Configuration</h3>
                <pre>
                    <code>SW1(config)# enable secret CCNA</code><br />
                    <code>SW1(config)# username admin secret CCNA</code><br />
                    <code>SW1(config)# access-list 1 permit host 192.168.2.1</code><br />
                    <code>SW1(config)# line vty 0 15</code><br />
                    <code>SW1(config-line)# login local</code><br />
                    <code>SW1(config-line)# exec-timeout 50</code><br />
                    <code>SW1(config-line)# transport input telnet</code><br />
                    <code>SW1(config-line)# access-class 1 in</code><br />
                </pre>


                <h2>FTP and TFTP</h2>
                <p>File Transfer Protocol (FTP) and Trivial File Transfer Protocol (TFTP) are industry standard protocols used to transfer files over a network.</p>
                <p>The most common use is to download the newer version of IOS from a server.</p>
                
                <h3>Trivial File Transfer Protocol </h3>
                <p>It was first standardized in 1981.</p>
                <p>Only allows a client to copy a file to or from a server.</p>
                <p>No authentication (Username/pw), so servers will respond to all TFTP requests. No encryption.</p>
                <p>TFTP servers listen on UDP 69.</p>
                
                <h4>TFTP Reliability</h4>
                <p>Every TFTP data message is acknowledged.</p>
                <p>Both client and server sends ACK messages.</p>
                
                <h4>TFTP ‘connections’</h4>
                <p>1. Connection: TFTP client sends a request to the servers and the server responds back, initializing the connections.</p>
                <p>2. Data Transfers: The client and server exchange TFTP messages. One sends data and the other sends acknowledgements.</p>
              
                <h4>TFTP TID</h4>
                <p>When the client sends the first message to the server, the destination port is UDP 69 and the source is a random ephemeral port.</p>
                <p>This random port is called a ‘Transfer Identifier’ (TID) and identifies the data transfer. </p>
                <p>The server then also selects a random TID to use as the source port when it replies, not 69.</p>
                <p>When client sends the next message, the destination port will be the server’s TID, not 69.</p>
                
                <h3>File Transfer Protocol (FTP)</h3>
                <p>FTP was first standardized in 1971. </p>
                <p>FTP uses TCP ports 20 and 21.</p>
                <p>Only Authentication no encryption.</p>
                <p>FTP over SSL/TLS (FTPS) and SSH File Transfer Protocol (SFTP) can be used for greater security.</p>
                <p>Clients can also navigate file directories, add and remove directories, list files etc using FTP.</p>
                <p>FTP control connections</p>
                <p>FTP uses two types of connections:</p>
                <ul>
                    <li>An FTP control connection (TCP 21) is established and used to send FTP commands and replies</li>
                    <li>When files and data are to be transferred, separate FTP data (TCP 20) connections are established and terminated as needed.</li>
                </ul>
                <p>Active mode: Server initiates the TCP connections. Default mode.</p>
                <p>Passive mode: Client initiates the data connections. </p>
                
                <h4>IOS File Systems</h4>
                <p>A file system is a way of controlling how data is stored and retrieved.</p>
                <p>Show file Systems</p>
                <ul>
                    <li>disk: storage devices such as flash memory</li>
                    <li>opaque: used for internal functions</li>
                    <li>nvram: Internal NVRAM. The startup-config file is stored here.</li>
                    <li>network: Represents external file systems, for example external FTP, TFTP servers.</li>
                </ul>
                <p>Upgrading Cisco IOS</p>
                <ul>
                    <li>Boot System</li>
                    <li>Reload</li>
                </ul>


                <h2>Network Address Translation (NAT)</h2>

                <p>NAT (Network Address Translation) is a method used by routers or firewalls to translate private IP addresses into public IP addresses, and vice versa.  </p>
                <p>NAT helps in IPv4 address conservation, allows private networks to access the Internet and hides internal IP addresses from external networks (basic security benefit).</p>
                <p>NAT typically runs on a router or firewall located at the boundary between a private network and the public Internet.</p>
                <p>It’s Interfaces are labeled as inside interface  private network and outside interface  public network.</p>
                <p>Internal device sends packet to internet, nat router changes source IP and records translation in NAT table. In case of return traffic, NAT router reverses translation and sends packet to correct internal device. </p>
                
                <h3>Static NAT</h3>
                <p>Fixed mapping, used when device must be unreachable from the internet and consumes one public IP per device.</p>
                
                <h3>Dynamic NAT</h3>
                <p>Maps public IPs from a pool.</p>
                <p>Temporary mappings, public IP assigned when traffic is initiated, limited by pool size and rarely used today.</p>
                <p>Network Address Translation (NAT)</p>

                <h3>Port Address Translation (PAT)</h3>
                <p>It is also called NAT Overload.</p>
                <p>Many ips share one public IP.</p>
                <p>Uses TCP/UDP port numbers to track sessions.</p>
                <p>Extremely efficient, used by home routers and enterprise networks and solves ipv4 exhaustion.</p>

                <h3>Terminology</h3>
                <p>Inside Local - Private IP of an internal host</p>
                <p>Inside Global - Public IP representing the internal host</p>
                <p>Outside Local - IP of an external host as seen internally</p>
                <p>Outside Global - Real Public IP of external host</p>
                
                <h2>Quality of Service (QoS)</h2>
                <p>Quality of Service (QoS) is a set of techniques used to manage and prioritize network traffic so that important applications get better performance when the network is congested.</p>
                <p>QoS does nothing when there is no congestion. It only matters when bandwidth is limited.</p>
                <p>VoIP/Video - Delay, jitter, packet loss. </p>
                <p>Streaming - Packet loss. </p>
                <p>Web browsing - Delay (tolerant). </p>
                <p>File transfer - Bandwidth (tolerant to delay)</p>
                
                <h3>Characteristics</h3>
                
                <p>Bandwidth: Amount of data that can be sent; measured in bps.</p>
                <p>Delay: time it takes for data to travel; voice/video are delay-sensitive.</p>
                <p>Jitter: variation in delay; very harmful to voice/video.</p>
                <p>Packet Loss: dropped packets due to congestion; affects all traffic especially real-time apps</p>
                
                <p>Qos flow:  Classify - Mark - Queue - Drop</p>
                
                <p>Classification Basis: Source/destination IP; Protocol (TCP, UDP), Application, Port number</p>
                
                <h4>Traffic Marking</h4>
                <p>Traffic are marked based on Differentiated Services Code Point (DSCP). Part of the IP header; 6-bit field; Values range from 0-63.</p>
                <p>EF (46) - Voice (Expedited Forwarding)</p>
                <p>AF - Assured Forwarding (business apps)</p>
                <p>BE (0) - Best Effort (default traffic)</p>
                
                <h4>Congestion Management (Queuing)</h4>
                <p>When congestion occurs, routers use queues. Basic queuing types:</p>
                <p>FIFO (First In, First Out) - default behaviour; no prioritization</p>
                <p>Priority Queuing (PQ) - High-priority traffic sent first; Risk of starvation</p>
                <p>Weighted Fair Queuing (WFQ) - Fair bandwidth distribution; Automatically prioritizes flows</p>
                
                <h4>Congestion Avoidance (Dropping)</h4> 
                <p>Prevents queues from filling completely.</p>
                <p>Random Early Detection (RED): Randomly drops packets before congestion is full; Signals TCP to slow down.</p>
                
                <h4>Policing vs Shaping: </h4>
                <p>Traffic Policing: Enforces bandwidth limits; Drops or marks excess traffic; Used by service providers.</p>
                <p>Traffic Shaping: Buffers excess traffic; Smooths traffic flow; Used by customers.</p>
                
                <p>QoS Trust Boundary: Point in the network where QoS markings are trusted; Usually at the access switch; </p>
            </article>
            </>
            )
}
